import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import React, { useEffect, useMemo, useState } from "react";

const ImageSlider = ({
  images,
  index,
  name,
  onImageClick,
  dataTestId,
  imageHeightClass = "max-h-96",
}) => {
  // Ensure images is always an array
  const imageArray = useMemo(
    () => (Array.isArray(images) ? images : images ? [images] : []),
    [images],
  );

  // Create a unique key for this slider in localStorage
  const storageKey = `${name}-${index}-slider-index`;

  // Initialize state from localStorage or default to 0
  const [currentIndex, setCurrentIndex] = useState(() => {
    const savedIndex = localStorage.getItem(storageKey);
    return savedIndex !== null ? parseInt(savedIndex, 10) : 0;
  });

  // Update localStorage when currentIndex changes
  useEffect(() => {
    localStorage.setItem(storageKey, currentIndex.toString());
  }, [currentIndex, storageKey]);

  // Reset index if we're out of bounds (e.g., if images array changed)
  useEffect(() => {
    if (currentIndex >= imageArray.length) {
      setCurrentIndex(0);
    }
  }, [imageArray, currentIndex]);

  const goToPrevious = () => {
    const isFirstSlide = currentIndex === 0;
    const newIndex = isFirstSlide ? imageArray.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
  };

  const goToNext = () => {
    const isLastSlide = currentIndex === imageArray.length - 1;
    const newIndex = isLastSlide ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
  };

  const goToSlide = (slideIndex) => {
    setCurrentIndex(slideIndex);
  };

  const getImageUrl = (item) => item?.imageUrl || "";

  if (!imageArray || imageArray.length === 0) return null;
  const currentImageUrl = getImageUrl(imageArray[currentIndex]);
  const currentLabel = imageArray[currentIndex]?.label;

  return (
    <div className="relative w-full mt-2">
      {currentLabel && (
        <div className="absolute top-0 right-0 m-2 inline-flex items-center rounded-md border border-transparent bg-gray-900 opacity-80 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm z-10">
          {currentLabel}
        </div>
      )}
      <img
        data-testid={dataTestId}
        src={currentImageUrl}
        alt={`${name}-${index + 1}-image-${currentIndex + 1}`}
        onClick={() => {
          if (onImageClick) {
            onImageClick(currentImageUrl);
          }
        }}
        className={`w-full h-auto ${imageHeightClass} object-contain rounded cursor-pointer mx-auto`}
      />

      {/* Navigation Arrows */}
      {imageArray.length > 1 && (
        <>
          <div
            className="absolute top-1/2 left-2 -translate-y-1/2 bg-black/50 p-1 rounded-full cursor-pointer hover:bg-black/70 z-10"
            onClick={goToPrevious}
          >
            <ChevronLeftIcon className="h-5 w-5 text-white" />
          </div>
          <div
            className="absolute top-1/2 right-2 -translate-y-1/2 bg-black/50 p-1 rounded-full cursor-pointer hover:bg-black/70 z-10"
            onClick={goToNext}
          >
            <ChevronRightIcon className="h-5 w-5 text-white" />
          </div>
        </>
      )}

      {/* Dots Indicator */}
      {imageArray.length > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-1 z-10">
          {imageArray.map((_, slideIndex) => (
            <div
              key={slideIndex}
              onClick={() => goToSlide(slideIndex)}
              className={`h-2 w-2 rounded-full cursor-pointer shadow-sm ${
                slideIndex === currentIndex ? "bg-white" : "bg-white/50"
              }`}
            ></div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageSlider;
